import React, { useState, useEffect } from 'react';
import { Button, Input, Label } from '../ui/UIComponents';
import { getPermissionCategories, updatePermissionCategories } from '../../services/rbacApi';

interface PermissionCategoriesManagerProps {
  applicationId: string;
  onUpdate?: () => void;
}

const PermissionCategoriesManager: React.FC<PermissionCategoriesManagerProps> = ({ 
  applicationId, 
  onUpdate 
}) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (applicationId) {
      fetchCategories();
    }
  }, [applicationId]);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPermissionCategories(applicationId);
      setCategories(data);
    } catch (err: any) {
      console.error('Error fetching permission categories:', err);
      setError('Failed to load permission categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    
    // Convert to lowercase and remove special characters
    const formattedCategory = newCategory.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    
    // Check if category already exists
    if (categories.includes(formattedCategory)) {
      setError('This category already exists');
      return;
    }
    
    setCategories([...categories, formattedCategory]);
    setNewCategory('');
  };

  const handleRemoveCategory = (category: string) => {
    setCategories(categories.filter(c => c !== category));
  };

  const handleSaveCategories = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updatePermissionCategories(applicationId, categories);
      if (onUpdate) onUpdate();
    } catch (err: any) {
      console.error('Error saving permission categories:', err);
      setError('Failed to save permission categories');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-5 mb-6">
      <h3 className="text-lg font-medium text-white mb-4">Permission Categories</h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500 text-red-300 rounded-lg">
          {error}
        </div>
      )}
      
      <div className="mb-5">
        <div className="flex items-end gap-3 mb-2">
          <div className="flex-1">
            <Label htmlFor="newCategory" className="block text-sm font-medium text-gray-300 mb-2">
              Add New Category
            </Label>
            <Input
              id="newCategory"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Enter category name"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>
          <Button
            onClick={handleAddCategory}
            disabled={!newCategory.trim()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Add
          </Button>
        </div>
        <p className="text-xs text-gray-400">
          Categories will be converted to lowercase with underscores (e.g., "User Management" → "user_management")
        </p>
      </div>
      
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-300 mb-2">Current Categories</h4>
        {isLoading ? (
          <p className="text-gray-400">Loading categories...</p>
        ) : categories.length === 0 ? (
          <p className="text-gray-400">No categories defined</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <div 
                key={category}
                className="flex items-center bg-gray-700 px-3 py-1 rounded-lg"
              >
                <span className="text-gray-200 mr-2">{category}</span>
                <button
                  onClick={() => handleRemoveCategory(category)}
                  className="text-gray-400 hover:text-red-400"
                  title="Remove category"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex justify-end">
        <Button
          onClick={handleSaveCategories}
          disabled={isSaving || isLoading}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
        >
          {isSaving ? 'Saving...' : 'Save Categories'}
        </Button>
      </div>
    </div>
  );
};

export default PermissionCategoriesManager;
